import { useState, useEffect, useCallback } from "react";
import BrandLogo from "@/components/BrandLogo";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Package, Truck, MapPin, Heart, Wallet, Tag, Star, HelpCircle,
  Settings, LogOut, Search, ShoppingCart, ChevronDown, ChevronRight,
  Edit2, Copy, ArrowRight, User, Mail, Phone, Calendar, X, Save,
  Eye, Building2, Gift, RotateCcw, FileDown, Crown,
  MessageCircle, Shield, Globe, Store, FileText, Bell, Smartphone,
} from "lucide-react";
import { getCartCount } from "@/utils/cart";
import ProductDataTab from "@/pages/account/ProductDataTab";
import MyPlansTab from "@/pages/account/MyPlansTab";
import PlanEnrollModal from "@/pages/account/PlanEnrollModal";
import MobileBottomNav from "@/components/MobileBottomNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtDateShort(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_CFG = {
  placed:           { label: "Placed",           bg: "bg-blue-50",   text: "text-blue-700" },
  accepted:         { label: "Processing",        bg: "bg-indigo-50", text: "text-indigo-700" },
  packed:           { label: "Packed",            bg: "bg-cyan-50",   text: "text-cyan-700" },
  shipped:          { label: "Shipped",           bg: "bg-blue-100",  text: "text-blue-800" },
  out_for_delivery: { label: "Out for Delivery",  bg: "bg-orange-50", text: "text-orange-700" },
  delivered:        { label: "Delivered",         bg: "bg-green-50",  text: "text-green-700" },
  cancelled:        { label: "Cancelled",         bg: "bg-red-50",    text: "text-red-600" },
  rejected:         { label: "Rejected",          bg: "bg-red-50",    text: "text-red-600" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "bg-slate-100", text: "text-slate-600" };
  return (
    <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
  );
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
function getToken()     { return localStorage.getItem("it_customer_token") || ""; }
function getUser()      { try { return JSON.parse(localStorage.getItem("it_customer_user") || "null"); } catch { return null; } }
function setUser(d)     { localStorage.setItem("it_customer_user", JSON.stringify(d)); }
function getWishlistCount() { try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]").length; } catch { return 0; } }
function getWishlistIds()   { try { return JSON.parse(localStorage.getItem("it_wishlist") || "[]"); } catch { return []; } }

// ── Storefront Header ─────────────────────────────────────────────────────────
function Header({ cartCount, wishlistCount, user, onSignOut }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-16">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 mr-2">
          <BrandLogo size="md" />
        </Link>

        {/* Search */}
        <form onSubmit={e => { e.preventDefault(); if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`); }}
          className="flex-1 max-w-2xl hidden sm:flex">
          <div className="flex w-full border-2 border-orange-400 rounded-full overflow-hidden">
            <div className="flex items-center pl-3.5 text-slate-400">
              <Search className="w-4 h-4" />
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search for products, categories, brands..."
              className="flex-1 px-3 py-2 text-sm focus:outline-none text-slate-700" />
            <button type="submit" className="bg-orange-500 hover:bg-orange-600 px-5 text-white text-xs font-bold transition-colors">
              Search
            </button>
          </div>
        </form>

        {/* Right icons */}
        <div className="ml-auto flex items-center gap-5 flex-shrink-0">
          {/* Track Order */}
          <Link to="/orders" className="hidden md:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors">
            <Truck className="w-4 h-4" />
            <span className="font-medium">Track Order</span>
          </Link>

          {/* Wishlist */}
          <Link to="/wishlist" className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-orange-500 transition-colors relative">
            <Heart className="w-4 h-4" />
            <span className="font-medium">Wishlist</span>
            {wishlistCount > 0 && (
              <span className="absolute -top-2 left-2.5 bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">{wishlistCount}</span>
            )}
          </Link>

          {/* Cart */}
          <Link to="/cart" className="relative text-slate-600 hover:text-orange-500 transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>
            )}
          </Link>

          {/* User dropdown */}
          <div className="relative">
            <button onClick={() => setShowMenu(v => !v)}
              className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 transition-colors">
              <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white text-xs font-black flex-shrink-0">
                {(user?.name || "U")[0].toUpperCase()}
              </div>
              <span className="hidden sm:block font-semibold">Hi, {user?.name?.split(" ")[0] || "User"}</span>
              <ChevronDown className="w-3.5 h-3.5 hidden sm:block text-slate-400" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-10 bg-white border border-slate-200 rounded-xl shadow-lg py-1 w-44 z-50">
                <Link to="/account" onClick={() => setShowMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  <User className="w-4 h-4 text-slate-400" /> My Account
                </Link>
                <Link to="/wishlist" onClick={() => setShowMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  <Heart className="w-4 h-4 text-slate-400" /> Wishlist
                </Link>
                <hr className="my-1 border-slate-100" />
                <button onClick={onSignOut} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// ── Sidebar Nav ───────────────────────────────────────────────────────────────
const NAV = [
  { key: "dashboard",     label: "My Account",       Icon: Home },
  { key: "orders",        label: "My Orders",         Icon: Package },
  { key: "track",         label: "Track Order",       Icon: Truck },
  { key: "addresses",     label: "My Addresses",      Icon: MapPin },
  { key: "wishlist",      label: "Wishlist",          Icon: Heart },
  { key: "wallet",        label: "My Wallet",         Icon: Wallet },
  { key: "coupons",       label: "My Coupons",        Icon: Tag },
  { key: "product-data",  label: "Product Data",      Icon: FileDown },
  { key: "my-plans",      label: "My Plans",          Icon: Crown },
  { key: "reviews",       label: "My Reviews",        Icon: Star },
  { key: "questions",     label: "My Questions",      Icon: HelpCircle },
  { key: "settings",      label: "Account Settings",  Icon: Settings },
];

function Sidebar({ active, onSelect, user, onSignOut }) {
  const initials = (user?.name || "U").split(" ").map(p => p[0]).join("").toUpperCase().slice(0, 2);
  return (
    <aside className="w-56 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col min-h-0">
      {/* Avatar + Name */}
      <div className="px-5 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-slate-700 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{user?.name || "Customer"}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email?.includes(".local") ? user?.phone || "" : (user?.email || "")}</p>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {NAV.map(({ key, label, Icon }) => {
          const isActive = active === key;
          return (
            <button key={key} onClick={() => onSelect(key)}
              data-testid={`account-nav-${key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all font-medium border-l-4 ${
                isActive ? "font-semibold border-l-4" : "text-slate-600 hover:bg-slate-50 border-transparent"
              }`}
              style={isActive ? {
                backgroundColor: "var(--brand-light, #fff5ee)",
                color: "var(--brand)",
                borderColor: "var(--brand)",
              } : {}}
            >
              <Icon className="w-4 h-4 flex-shrink-0" style={isActive ? { color: "var(--brand)" } : { color: "#94a3b8" }} />
              {label}
            </button>
          );
        })}
        <button onClick={onSignOut}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 font-medium border-l-4 border-transparent transition-colors">
          <LogOut className="w-4 h-4 text-red-400 flex-shrink-0" />
          Logout
        </button>
      </nav>
    </aside>
  );
}

// ── Dashboard Overview ────────────────────────────────────────────────────────
function Dashboard({ profile, orders, coupons, wishlistProducts, onTabChange }) {
  const totalOrders = orders.length;
  const wCount = getWishlistCount();

  const recentOrders = orders.slice(0, 5);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}"`);
  };

  return (
    <div className="space-y-5">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-100 rounded-2xl overflow-hidden px-8 py-7 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900 mb-1">Hello, {profile?.name?.split(" ")[0] || "there"}!</h1>
          <p className="text-sm text-slate-500 max-w-md">Welcome to your account. Manage your orders, addresses, wishlist and more.</p>
        </div>
        {/* Illustration */}
        <div className="hidden md:flex items-end gap-2 pr-4 opacity-80">
          <svg width="120" height="90" viewBox="0 0 120 90" fill="none">
            <rect x="30" y="30" width="36" height="48" rx="4" fill="#F97316" opacity="0.8"/>
            <rect x="32" y="18" width="32" height="14" rx="3" fill="#EA580C"/>
            <rect x="45" y="14" width="6" height="6" rx="3" fill="#C2410C"/>
            <rect x="60" y="38" width="32" height="42" rx="4" fill="#3B82F6" opacity="0.8"/>
            <rect x="62" y="26" width="28" height="14" rx="3" fill="#2563EB"/>
            <rect x="74" y="22" width="6" height="6" rx="3" fill="#1D4ED8"/>
            <rect x="12" y="50" width="24" height="30" rx="3" fill="#F59E0B" opacity="0.7"/>
            <rect x="14" y="40" width="20" height="12" rx="2" fill="#D97706"/>
            <circle cx="100" cy="20" r="8" fill="#10B981" opacity="0.3"/>
            <circle cx="108" cy="30" r="5" fill="#10B981" opacity="0.4"/>
            <path d="M96 18 Q100 10 104 18" stroke="#059669" strokeWidth="1.5" fill="none"/>
          </svg>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Orders",     value: totalOrders, sub: "View Orders →",   Icon: Package,  iconBg: "bg-blue-100",  iconColor: "text-blue-600",  key: "orders"    },
          { label: "Wishlist Items",   value: wCount,      sub: "View Wishlist →", Icon: Heart,    iconBg: "bg-red-100",   iconColor: "text-red-500",   key: "wishlist"  },
          { label: "Saved Coupons",    value: coupons.length, sub: "View Coupons →",  Icon: Tag,   iconBg: "bg-green-100", iconColor: "text-green-600", key: "coupons"   },
          { label: "Saved Addresses",  value: orders.length > 0 ? 1 : 0, sub: "View Addresses →", Icon: MapPin, iconBg: "bg-orange-100", iconColor: "text-orange-500", key: "addresses" },
        ].map(card => {
          const CIcon = card.Icon;
          return (
            <div key={card.key} className="bg-white border border-slate-200 rounded-xl px-5 py-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg}`}>
                  <CIcon className={`w-5 h-5 ${card.iconColor}`} />
                </div>
              </div>
              <p className="text-2xl font-black text-slate-900">{card.value}</p>
              <p className="text-xs text-slate-500 mb-2">{card.label}</p>
              <button onClick={() => onTabChange(card.key)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5 transition-colors">
                {card.sub.replace(" →", "")} <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Main two-column */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Recent Orders (3/5) */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-900">Recent Orders</h2>
            <button onClick={() => onTabChange("orders")}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
              View All Orders <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {recentOrders.length === 0 ? (
            <div className="py-12 text-center">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-200" />
              <p className="text-sm text-slate-400">No orders yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-slate-100 bg-slate-50">
                    <th className="px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide whitespace-nowrap">Order ID</th>
                    <th className="px-3 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">Date</th>
                    <th className="px-3 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">Items</th>
                    <th className="px-3 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">Amount</th>
                    <th className="px-3 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">Status</th>
                    <th className="px-3 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {recentOrders.map(order => {
                    const firstItem = order.items?.[0];
                    const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
                    return (
                      <tr key={order.id} className="hover:bg-slate-50 transition-colors" data-testid={`recent-order-${order.id}`}>
                        <td className="px-4 py-3">
                          <span className="text-xs font-bold text-blue-600">{order.order_number}</span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <p className="text-xs text-slate-700">{fmtDateShort(order.created_at)}</p>
                          <p className="text-[10px] text-slate-400">{new Date(order.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</p>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            {firstItem?.image ? (
                              <img src={firstItem.image} alt="" className="w-8 h-8 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Package className="w-4 h-4 text-slate-300" />
                              </div>
                            )}
                            <div>
                              <p className="text-xs text-slate-700 leading-tight line-clamp-1 max-w-[100px]">{firstItem?.product_name || firstItem?.name || "Product"}</p>
                              <p className="text-[10px] text-slate-400">x{itemCount}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-xs font-bold text-slate-900">₹{fmt(order.total)}</span>
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge status={order.order_status} />
                        </td>
                        <td className="px-3 py-3">
                          <Link to={`/orders/${order.order_number}`}
                            className="text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg px-2.5 py-1 hover:bg-slate-50 hover:border-slate-400 transition-colors whitespace-nowrap">
                            View Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Account Info + Address (2/5) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Account Info */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-900">Account Information</h2>
              <button onClick={() => onTabChange("settings")}
                className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
                <Edit2 className="w-3 h-3" /> Edit
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {[
                { Icon: User,     label: "Full Name",      value: profile?.name || "—" },
                { Icon: Mail,     label: "Email Address",  value: profile?.email?.includes(".local") ? "—" : (profile?.email || "—") },
                { Icon: Phone,    label: "Mobile Number",  value: profile?.phone || "—" },
                { Icon: Calendar, label: "Date of Birth",  value: profile?.dob || "—" },
              ].map(row => (
                <div key={row.label} className="flex items-start gap-3">
                  <row.Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-400 font-medium">{row.label}</p>
                    <p className="text-xs font-semibold text-slate-800 truncate">{row.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Default Address */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <h2 className="text-sm font-bold text-slate-900">Default Address</h2>
              <button onClick={() => onTabChange("addresses")}
                className="text-xs font-semibold text-blue-600 hover:text-blue-800">Manage Addresses</button>
            </div>
            {orders.length > 0 ? (
              <div className="px-5 py-4">
                <div className="border border-slate-200 rounded-xl p-3.5 relative">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-slate-800 mb-1">{orders[0].customer_name}</p>
                        <p className="text-xs text-slate-500 leading-relaxed">{orders[0].delivery_address}</p>
                        {orders[0].customer_phone && <p className="text-xs text-slate-500 mt-1">{orders[0].customer_phone}</p>}
                      </div>
                    </div>
                    <span className="flex-shrink-0 text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Default</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-5 py-6 text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-xs text-slate-400">No saved addresses yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom 3-column row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pb-8">
        {/* My Wishlist */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-900">My Wishlist</h2>
            <Link to="/wishlist" className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="p-4">
            {wishlistProducts.length === 0 ? (
              <div className="py-6 text-center">
                <Heart className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-xs text-slate-400">Your wishlist is empty</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {wishlistProducts.slice(0, 4).map(p => (
                  <Link key={p.id} to={`/products/${p.url_slug || p.id}`}
                    className="block group" data-testid={`wishlist-preview-${p.id}`}>
                    <div className="aspect-square rounded-xl overflow-hidden bg-slate-50 border border-slate-100 relative">
                      <img src={p.main_image || ""} alt={p.item_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                      <div className="absolute top-1 right-1">
                        <Heart className="w-3 h-3 text-red-500 fill-red-500" />
                      </div>
                    </div>
                    <p className="text-[10px] font-semibold text-slate-700 mt-1 text-center">₹{fmt(p.deal_price_incl_gst)}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* My Coupons */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-900">My Coupons</h2>
            <button onClick={() => onTabChange("coupons")}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-0.5">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {coupons.length === 0 ? (
              <div className="py-6 text-center">
                <Tag className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-xs text-slate-400">No active coupons</p>
              </div>
            ) : coupons.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-start gap-3 px-4 py-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-slate-900 font-mono">{c.code}</p>
                  <p className="text-[10px] text-slate-500 leading-tight">{c.description || (c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`)}</p>
                  {c.expires_at && <p className="text-[10px] text-slate-400 mt-0.5">Valid till {fmtDateShort(c.expires_at)}</p>}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success(`Copied "${c.code}"`); }}
                  className="flex-shrink-0 text-[10px] font-bold border border-orange-300 text-orange-600 hover:bg-orange-50 px-2.5 py-1 rounded-lg transition-colors">
                  Copy Code
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* My Wallet */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-900">My Wallet</h2>
          </div>
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xl font-black text-slate-900">₹0</p>
                  <p className="text-[10px] text-slate-400">Available Balance</p>
                </div>
              </div>
              <button className="text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-colors">
                View Transactions
              </button>
            </div>
            <div className="space-y-2">
              {[
                { label: "Refunds", value: "₹0", Icon: RotateCcw },
                { label: "Gift Card", value: "₹0", Icon: Gift },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between py-2 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <item.Icon className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-600">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-semibold text-slate-800">{item.value}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── My Orders Tab ─────────────────────────────────────────────────────────────
function MyOrdersTab({ orders }) {
  const FILTERS = [
    { id: "all",       label: "All",        statuses: null },
    { id: "active",    label: "Active",     statuses: ["placed","accepted","packed","shipped","out_for_delivery"] },
    { id: "delivered", label: "Delivered",  statuses: ["delivered"] },
    { id: "cancelled", label: "Cancelled",  statuses: ["cancelled","rejected"] },
  ];
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? orders : orders.filter(o => FILTERS.find(f => f.id === filter)?.statuses?.includes(o.order_status));

  return (
    <div data-testid="my-orders-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-black text-slate-900">My Orders</h2>
        <div className="flex gap-2">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`order-filter-${f.id}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${filter === f.id ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Package className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm font-semibold text-slate-500">No orders found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(order => {
            const items = order.items || [];
            const itemCount = items.reduce((s, i) => s + (i.quantity || 1), 0);
            return (
              <div key={order.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow"
                data-testid={`order-item-${order.id}`}>
                <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-blue-600">{order.order_number}</span>
                      <StatusBadge status={order.order_status} />
                    </div>
                    <p className="text-xs text-slate-500">{itemCount} item{itemCount !== 1 ? "s" : ""} · ₹{fmt(order.total)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(order.created_at)}</p>
                  </div>
                  <Link to={`/orders/${order.order_number}`}
                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded-xl px-3.5 py-2 hover:bg-slate-50 transition-colors">
                    <Eye className="w-3.5 h-3.5" /> View Details
                  </Link>
                </div>
                {items.length > 0 && (
                  <div className="border-t border-slate-50 px-5 py-3 flex gap-2 overflow-x-auto">
                    {items.slice(0, 4).map((item, i) => (
                      <div key={i} className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-9 h-9 bg-slate-100 rounded-lg overflow-hidden border border-slate-100">
                          {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-slate-300 m-auto mt-2" />}
                        </div>
                        <p className="text-[11px] text-slate-600 max-w-[80px] line-clamp-2 leading-tight">{item.product_name || item.name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Track Order Tab ───────────────────────────────────────────────────────────
function TrackOrderTab() {
  const navigate = useNavigate();
  const [orderNum, setOrderNum] = useState("");
  return (
    <div className="max-w-md">
      <h2 className="text-lg font-black text-slate-900 mb-5">Track Order</h2>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <p className="text-sm text-slate-600 mb-4">Enter your Order ID to track the status of your order.</p>
        <div className="space-y-3">
          <input value={orderNum} onChange={e => setOrderNum(e.target.value.toUpperCase())}
            placeholder="e.g. ORD-2026-123456"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 font-mono" />
          <button onClick={() => { if (orderNum.trim()) navigate(`/orders/${orderNum.trim()}`); else toast.error("Enter an Order ID"); }}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">
            <Truck className="w-4 h-4" /> Track Order
          </button>
        </div>
      </div>
    </div>
  );
}

// ── My Addresses Tab ──────────────────────────────────────────────────────────
function MyAddressesTab({ orders }) {
  const unique = orders.reduce((acc, o) => {
    if (o.delivery_address && !acc.find(a => a.address === o.delivery_address))
      acc.push({ name: o.customer_name, address: o.delivery_address, phone: o.customer_phone });
    return acc;
  }, []);
  return (
    <div>
      <h2 className="text-lg font-black text-slate-900 mb-5">My Addresses</h2>
      {unique.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <MapPin className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm font-semibold text-slate-500">No saved addresses yet</p>
          <p className="text-xs text-slate-400 mt-1">Addresses from your orders will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {unique.map((a, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <p className="text-sm font-bold text-slate-900">{a.name}</p>
                </div>
                {i === 0 && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Default</span>}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed pl-6">{a.address}</p>
              {a.phone && <p className="text-xs text-slate-400 mt-1 pl-6">{a.phone}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── My Coupons Tab ────────────────────────────────────────────────────────────
function MyCouponsTab({ coupons }) {
  return (
    <div>
      <h2 className="text-lg font-black text-slate-900 mb-5">My Coupons</h2>
      {coupons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <Tag className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm font-semibold text-slate-500">No active coupons available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {coupons.map(c => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-sm transition-shadow">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 px-5 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-black font-mono text-orange-600 tracking-wider">{c.code}</span>
                  <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success(`Copied "${c.code}"`); }}
                    className="text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg transition-colors">
                    Copy Code
                  </button>
                </div>
              </div>
              <div className="px-5 py-3">
                <p className="text-sm font-semibold text-slate-700">
                  {c.description || (c.type === "percent" ? `${c.value}% off on all orders` : `Flat ₹${c.value} off`)}
                </p>
                {c.min_order > 0 && <p className="text-xs text-slate-400 mt-1">Min. order ₹{fmt(c.min_order)}</p>}
                {c.expires_at && <p className="text-xs text-slate-400 mt-0.5">Valid till {fmtDateShort(c.expires_at)}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Account Settings Tab ──────────────────────────────────────────────────────
function AccountSettingsTab({ profile, token, onUpdate }) {
  const [name, setName] = useState(profile?.name || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(profile?.name || ""); }, [profile]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customer/profile`, { name: name.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate({ name: res.data.name });
      toast.success("Profile updated!");
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg">
      <h2 className="text-lg font-black text-slate-900 mb-5">Account Settings</h2>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Full Name</label>
          <input value={name} onChange={e => setName(e.target.value)} data-testid="settings-name-input"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500" />
        </div>
        {profile?.email && !profile.email.includes(".local") && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Email Address</label>
            <input value={profile.email} readOnly className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-50 text-slate-500" />
          </div>
        )}
        {profile?.phone && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Mobile Number</label>
            <input value={profile.phone} readOnly className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-slate-50 text-slate-500" />
          </div>
        )}
        <button onClick={handleSave} disabled={saving} data-testid="settings-save-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-bold rounded-xl transition-colors">
          {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ── Placeholder Tabs ──────────────────────────────────────────────────────────
function PlaceholderTab({ label, Icon, message }) {
  return (
    <div>
      <h2 className="text-lg font-black text-slate-900 mb-5">{label}</h2>
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
        <Icon className="w-10 h-10 mx-auto mb-3 text-slate-200" />
        <p className="text-sm text-slate-400">{message}</p>
      </div>
    </div>
  );
}

// ── Mobile Icon Helper ───────────────────────────────────────────────────────
const ICON_MAP = {
  Package, Heart, Tag, HelpCircle, MapPin, Bell, Star, MessageCircle,
  Shield, Globe, Store, FileText, User, Settings, Wallet, RotateCcw,
  Gift, Eye, Truck, LogOut, ChevronRight, Home, Search, Mail,
  ShoppingCart, Phone, Smartphone, Building2, Crown, Edit2,
};
function MobileIcon({ name, className }) {
  const Ic = ICON_MAP[name] || ChevronRight;
  return <Ic className={className} />;
}

// ── Mobile Account Page ──────────────────────────────────────────────────────
// Mobile menu links: map legacy/placeholder URLs to real pages so nothing is dead or blank.
// Applied at render time, so it also covers menus saved from the admin panel.
const MOBILE_URL_FIXES = { "/faq": "/pages/contact-us", "/help": "/pages/contact-us", "/terms": "/pages/terms-of-service" };
const NAV_TAB_KEYS = new Set(NAV.map(n => n.key));

function resolveMobileTarget(item, isLoggedIn) {
  if (!item.url || item.url === "#") {
    if (item.id === "notifications" && isLoggedIn) return { url: "/account", tab: "settings" };
    return null; // no destination yet: hide instead of showing a dead button
  }
  return { url: MOBILE_URL_FIXES[item.url] || item.url, tab: item.tab || "" };
}

function isVisibleMobileItem(item, isLoggedIn) {
  if (!item.enabled) return false;
  const t = resolveMobileTarget(item, isLoggedIn);
  if (!t) return false;
  // Logged-in tab links are already in the "My Account" list (same as the desktop sidebar)
  if (isLoggedIn && t.url === "/account" && NAV_TAB_KEYS.has(t.tab)) return false;
  return true;
}

function MobileAccountView({ token, user, settings, onSignOut, onNavigate }) {
  const navigate = useNavigate();
  const isLoggedIn = !!token;

  // Sections to show
  const sections = (settings?.sections || []).filter(s => {
    if (!s.enabled) return false;
    if (s.show_when === "logged_in")  return isLoggedIn;
    if (s.show_when === "logged_out") return !isLoggedIn;
    return true; // "always"
  })
    .map(s => ({ ...s, items: (s.items || []).filter(i => isVisibleMobileItem(i, isLoggedIn)) }))
    .filter(s => s.items.length > 0);

  const handleItemClick = (item) => {
    const t = resolveMobileTarget(item, isLoggedIn);
    if (!t) return;
    if (t.url === "/account" && t.tab) { onNavigate(t.tab); return; }
    navigate(t.url);
  };


  return (
    <div className="bg-[#f1f3f6] min-h-screen pb-20" data-testid="mobile-account-page">
      {/* Header */}
      <header className="bg-white sticky top-0 z-40 shadow-sm">
        <div className="flex items-center px-4 py-3.5 gap-3">
          <button onClick={() => navigate(-1)} className="p-1 -ml-1 rounded-full hover:bg-slate-100 transition-colors">
            <ChevronRight className="w-5 h-5 text-slate-700 rotate-180" />
          </button>
          <span className="text-base font-bold text-slate-900 flex-1">Account</span>
          <Link to="/">
            <BrandLogo size="sm" />
          </Link>
        </div>
      </header>

      <div className="px-0 py-2 space-y-2">
        {/* ── LOGGED OUT: login prompt ── */}
        {!isLoggedIn && (
          <div className="bg-white px-4 py-4 flex items-center justify-between gap-3 shadow-sm">
            <p className="text-sm font-semibold text-slate-800 flex-1">
              {settings?.login_prompt?.text || "Log in to get exclusive offers"}
            </p>
            <Link
              to="/customer-login"
              data-testid="mobile-login-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-full flex-shrink-0 transition-colors"
            >
              {settings?.login_prompt?.button_text || "Log In"}
            </Link>
          </div>
        )}

        {/* ── LOGGED IN: user profile card ── */}
        {isLoggedIn && user && (
          <div className="bg-white px-4 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-black text-orange-600">
                  {(user.name || user.email || "U")[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-slate-900 truncate">{user.name || "User"}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
              <button
                onClick={() => onNavigate("settings")}
                className="text-xs font-bold text-orange-500 border border-orange-300 px-3 py-1.5 rounded-full hover:bg-orange-50 transition-colors flex-shrink-0"
                data-testid="mobile-account-edit-profile"
              >Edit</button>
            </div>
          </div>
        )}

        {/* ── LOGGED IN: quick actions 2×2 grid ── */}
        {isLoggedIn && (settings?.quick_actions || []).filter(a => a.enabled).length > 0 && (
          <div className="bg-white px-4 py-3 shadow-sm">
            <div className="grid grid-cols-2 gap-2.5">
              {(settings.quick_actions || []).filter(a => a.enabled).slice(0, 4).map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleItemClick(action)}
                  data-testid={`mobile-quick-action-${action.id}`}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                >
                  <MobileIcon name={action.icon} className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800 truncate">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── LOGGED IN: same menu as the desktop sidebar ── */}
        {isLoggedIn && (
          <div className="bg-white shadow-sm" data-testid="mobile-account-nav">
            <div className="px-4 py-2.5 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">My Account</p>
            </div>
            <div className="divide-y divide-slate-100">
              {NAV.filter(n => n.key !== "dashboard").map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => onNavigate(key)}
                  data-testid={`mobile-account-nav-${key}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <p className="flex-1 text-sm font-medium text-slate-800">{label}</p>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Dynamic sections ── */}
        {sections.map((section) => (
          <div key={section.id} className="bg-white shadow-sm">
            {section.title && (
              <div className="px-4 py-2.5 border-b border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{section.title}</p>
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {(section.items || []).filter(i => i.enabled).map((item, idx) => (
                <button
                  key={item.id || idx}
                  onClick={() => handleItemClick(item)}
                  data-testid={`mobile-account-item-${item.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <MobileIcon name={item.icon} className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{item.label}</p>
                    {item.subtitle && <p className="text-xs text-slate-400 mt-0.5">{item.subtitle}</p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* ── Logout button ── */}
        {isLoggedIn && (
          <div className="bg-white shadow-sm">
            <button
              onClick={onSignOut}
              data-testid="mobile-account-logout-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-4 text-red-500 hover:bg-red-50 active:bg-red-100 transition-colors font-bold text-sm"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [cartCount, setCartCount] = useState(getCartCount);
  const [wishlistCount, setWishlistCount] = useState(getWishlistCount);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const token = localStorage.getItem("it_customer_token") || "";
  const [mobileSettings, setMobileSettings] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Fetch mobile account settings
  useEffect(() => {
    axios.get(`${API}/mobile-account-settings`).then(r => setMobileSettings(r.data)).catch(() => {});
  }, []);

  // Redirect if not logged in (desktop only; mobile shows logged-out state)
  useEffect(() => {
    if (!token && !isMobile) { toast.error("Please sign in first"); navigate("/customer-login"); }
  }, [token, navigate, isMobile]);

  // Support deep-link to a specific tab via navigation state or query param
  useEffect(() => {
    const stateTab = location.state?.tab;
    if (stateTab) { setActiveTab(stateTab); window.history.replaceState({}, ""); }
  }, [location.state]);

  // Cart + wishlist live counts
  useEffect(() => {
    const updateCart = () => setCartCount(getCartCount());
    const updateWl = () => setWishlistCount(getWishlistCount());
    window.addEventListener("cart-updated", updateCart);
    window.addEventListener("wishlist-updated", updateWl);
    return () => { window.removeEventListener("cart-updated", updateCart); window.removeEventListener("wishlist-updated", updateWl); };
  }, []);

  // Fetch profile, orders, coupons in parallel
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/customer/profile`, { headers }).then(r => setProfile(r.data)).catch(() => {}),
      axios.get(`${API}/customer/orders`, { headers }).then(r => setOrders(r.data)).catch(() => {}),
      axios.get(`${API}/coupons/public`).then(r => setCoupons(r.data)).catch(() => {}),
    ]);
  }, [token]);

  // Fetch wishlist product previews
  useEffect(() => {
    const ids = getWishlistIds().slice(0, 4);
    if (!ids.length) return;
    Promise.all(ids.map(id => axios.get(`${API}/products/public/${id}`).then(r => r.data).catch(() => null)))
      .then(results => setWishlistProducts(results.filter(Boolean)));
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("it_customer_token");
    localStorage.removeItem("it_customer_user");
    toast.success("Signed out");
    navigate("/");
  };

  const handleTabChange = (tab) => {
    if (tab === "wishlist") { navigate("/wishlist"); return; }
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleProfileUpdate = (data) => {
    setProfile(prev => ({ ...prev, ...data }));
    const cu = getUser();
    if (cu) setUser({ ...cu, ...data });
  };

  if (!token && !isMobile) return null;

  // Same tab screens on desktop (sidebar) and mobile (opened from the account menu)
  const tabContent = (
    <>
          {activeTab === "dashboard" && (
            <Dashboard
              profile={profile}
              orders={orders}
              coupons={coupons}
              wishlistProducts={wishlistProducts}
              onTabChange={handleTabChange}
            />
          )}
          {activeTab === "orders" && <MyOrdersTab orders={orders} />}
          {activeTab === "track" && <TrackOrderTab />}
          {activeTab === "addresses" && <MyAddressesTab orders={orders} />}
          {activeTab === "coupons" && <MyCouponsTab coupons={coupons} />}
          {activeTab === "settings" && (
            <AccountSettingsTab profile={profile} token={token} onUpdate={handleProfileUpdate} />
          )}
          {activeTab === "wallet" && (
            <PlaceholderTab label="My Wallet" Icon={Wallet} message="Wallet feature coming soon." />
          )}
          {activeTab === "reviews" && (
            <PlaceholderTab label="My Reviews" Icon={Star} message="Your product reviews will appear here." />
          )}
          {activeTab === "questions" && (
            <PlaceholderTab label="My Questions" Icon={HelpCircle} message="Your product questions will appear here." />
          )}
          {activeTab === "product-data" && (
            <ProductDataTab token={token} onNeedPlan={() => setShowEnrollModal(true)} />
          )}
          {activeTab === "my-plans" && (
            <MyPlansTab token={token} onEnrollClick={() => setShowEnrollModal(true)} />
          )}
    </>
  );

  return (
    <>
      {/* ═══════════════ MOBILE ACCOUNT ═══════════════ */}
      <div className="block md:hidden">
        {isMobile && token && activeTab !== "dashboard" ? (
          <div className="bg-[#f1f3f6] min-h-screen pb-20" data-testid="mobile-account-tab">
            <header className="bg-white sticky top-0 z-40 shadow-sm">
              <div className="flex items-center px-4 py-3.5 gap-3">
                <button
                  onClick={() => setActiveTab("dashboard")}
                  className="p-1 -ml-1 rounded-full hover:bg-slate-100 transition-colors"
                  data-testid="mobile-account-tab-back"
                >
                  <ChevronRight className="w-5 h-5 text-slate-700 rotate-180" />
                </button>
                <span className="text-base font-bold text-slate-900 flex-1">
                  {(NAV.find(n => n.key === activeTab) || {}).label || "Account"}
                </span>
              </div>
            </header>
            <div className="p-3">{tabContent}</div>
            <MobileBottomNav />
          </div>
        ) : (
          <MobileAccountView
            token={token}
            user={profile || getUser()}
            settings={mobileSettings}
            onSignOut={handleSignOut}
            onNavigate={handleTabChange}
          />
        )}
      </div>

      {/* ═══════════════ DESKTOP ACCOUNT ══════════════ */}
      <div className={`hidden md:block min-h-screen bg-slate-50 ${!token ? "opacity-0 pointer-events-none" : ""}`}>
      <Header
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        user={profile || getUser()}
        onSignOut={handleSignOut}
      />

      <div className="max-w-7xl mx-auto flex" style={{ minHeight: "calc(100vh - 64px)" }}>
        {/* Sidebar */}
        <Sidebar
          active={activeTab}
          onSelect={handleTabChange}
          user={profile || getUser()}
          onSignOut={handleSignOut}
        />

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-6 overflow-auto">
          {!isMobile && tabContent}
        </main>
      </div>

      {showEnrollModal && (
        <PlanEnrollModal
          token={token}
          onClose={() => setShowEnrollModal(false)}
          onSuccess={() => { setShowEnrollModal(false); setActiveTab("my-plans"); }}
        />
      )}
      </div>{/* end desktop wrapper */}
    </>
  );
}
